import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { NotificationItem } from "@/hooks/useNotifications";
import { TOOFI_CARD_VARIANT_CLASS } from "@/lib/toofi-ux-tokens";
import { cn } from "@/lib/utils";
import { BrainCircuit } from "lucide-react";

const formatEvidenceReason = (notification: NotificationItem) => {
  const evidence = notification.nudgeEvidence ?? {};
  switch (notification.nudgeEventType) {
    case "review_due":
      return evidence["concept"] ? `Review is due for ${String(evidence["concept"])}.` : "Review is due on a weak or overdue topic.";
    case "plan_reminder":
      return evidence["planTitle"] ? `Your active plan "${String(evidence["planTitle"])}" still has work due.` : "Your active study plan still has work due.";
    case "deadline_pressure":
      return evidence["targetDate"] ? `A target date is getting close: ${String(evidence["targetDate"])}.` : "A deadline or exam target is getting close.";
    case "session_resume":
      return evidence["resourceTitle"] ? `You recently left ${String(evidence["resourceTitle"])} mid-flow.` : "You left a recent study flow unfinished.";
    case "celebration":
      return evidence["concept"] ? `You improved on ${String(evidence["concept"])} recently.` : "You recently made visible study progress.";
    case "streak_break":
      return "You have been inactive long enough that your study rhythm may be slipping.";
    default:
      return "Toofi found a clear study reason to check in.";
  }
};

export function ToofiNudgeDetailDrawer({
  open,
  onOpenChange,
  notification,
  onOpenTarget,
  onSnoozeThreeHours,
  onSnoozeUntilTomorrow,
  onPauseForDay,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notification: NotificationItem | null;
  onOpenTarget: () => void;
  onSnoozeThreeHours: () => void;
  onSnoozeUntilTomorrow: () => void;
  onPauseForDay: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-[1.5rem] border border-white/75 bg-[linear-gradient(180deg,rgba(255,252,245,0.98),rgba(248,250,252,0.94))] p-0 shadow-[0_24px_60px_rgba(15,23,42,0.20)] backdrop-blur-2xl">
        {notification ? (
          <div className="space-y-5 p-5">
            <DialogHeader className="space-y-3 text-left">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <BrainCircuit className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-[1.1rem] tracking-[-0.02em] text-slate-950">{notification.title}</DialogTitle>
                <DialogDescription className="mt-1 text-[13px] leading-6 text-slate-500">
                  {notification.message}
                </DialogDescription>
              </div>
            </DialogHeader>

            <section className={cn("p-4", TOOFI_CARD_VARIANT_CLASS.entry)}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Why this?</p>
              <p className="mt-2 text-[13px] leading-6 text-slate-700">{formatEvidenceReason(notification)}</p>
              {notification.explanation ? (
                <p className="mt-2 text-[12px] leading-5 text-slate-500">{notification.explanation}</p>
              ) : null}
            </section>

            <div className="grid gap-2">
              <Button type="button" className="h-10 rounded-full bg-slate-900 text-[12px] text-white hover:bg-slate-900/95" onClick={onOpenTarget}>
                Open
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="secondary" className="h-10 rounded-full text-[12px]" onClick={onSnoozeThreeHours}>
                  Snooze 3 hours
                </Button>
                <Button type="button" variant="secondary" className="h-10 rounded-full text-[12px]" onClick={onSnoozeUntilTomorrow}>
                  Snooze to tomorrow
                </Button>
              </div>
              <Button type="button" variant="ghost" className="h-10 rounded-full border border-transparent bg-transparent text-[12px] text-slate-600 hover:bg-white/70" onClick={onPauseForDay}>
                Pause Toofi nudges for 24 hours
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
